// ============================================================================
// Class registry — used to break circular module dependencies between model
// classes that reference each other via class-transformer's `@Type(() => X)`
// decorator (most prominently `Session` <-> `Submission`).
//
// The decorator needs the RUNTIME class object (not just the type) inside its
// factory callback. A naïve `import { Other } from './other'` on each side
// creates a module-level cycle that webpack flags at build time.
//
// To break the cycle without changing runtime behaviour:
//
//   1. Each model file declares its own class without importing the
//      counterpart as a VALUE. Type-only imports (`import type { ... }`) are
//      fine because they're erased by the TypeScript compiler and never
//      appear in webpack's dependency graph.
//
//   2. After the class is declared, the file calls `register('Name', Cls)`
//      (effectful side-effect at module evaluation time).
//
//   3. Inside the `@Type` factory, the file calls `getClass<Other>('Other')`
//      to fetch the live constructor. The factory only runs at
//      deserialization time, by which point both modules have been
//      evaluated and the registry is populated.
//
// Webpack sees both `paper.ts` and `session.ts` importing `_class-registry.ts`
// (a leaf module with no back-edges) — acyclic graph, no warning.
//
// Limitations:
//   - Class names are strings; typos won't be caught at compile time.
//     Mitigate by exporting a typed name constant per registered class
//     (e.g. `export const REG_SUBMISSION = 'Submission'`).
//   - `getClass` returns `unknown` and requires a type assertion at the
//     call site. The cast is documentation, not a guarantee.
// ============================================================================

const registry: Record<string, unknown> = {};

export function register(name: string, cls: unknown): void {
  registry[name] = cls;
}

export function getClass<T = unknown>(name: string): T {
  const cls = registry[name];
  if (!cls) {
    // Throwing here helps surface ordering bugs — e.g. a deserialization
    // happening before both model modules have been evaluated. In
    // practice this shouldn't trigger because deserialization is a
    // user-driven runtime event (network response, etc.), long after
    // module init.
    throw new Error(`[class-registry] '${name}' not registered`);
  }
  return cls as T;
}
